import dayjs from 'dayjs';
import { useGetTaskQuery } from '@/modules/common/queries/tasks.ts';
import { priorityOptions } from '@/modules/admin-dashboard/components/modals/TaskModal.tsx';
import { Avatar, Spinner, TextWithShortLinks } from 'ui';
import { TaskListWithRelationsResponse } from 'compliance-api';

interface TaskItemDetailedInfoProps {
  task: TaskListWithRelationsResponse;
}

const TaskItemDetailedInfo = ({ task }: TaskItemDetailedInfoProps) => {
  const { data: detailedTask } = useGetTaskQuery(task.id);

  return (
    <div className="p-4 border-t space-y-4">
      {detailedTask ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-2">
              <div className="text-gray-500 text-xs">Priority</div>
              <div className="text-sm">
                {priorityOptions.find(item => item.value == task?.priority)?.label}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-gray-500 text-xs">Does every user need to complete</div>
              <div className="text-sm">
                {task.is_every_employee_needs_to_complete ? 'Yes' : 'No'}
              </div>
            </div>
            <div className="space-y-2 min-w-[100px]">
              <div className="text-gray-500 text-xs">Event</div>
              <div className="text-sm">{detailedTask?.task?.incident_result?.name || '-'}</div>
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="space-y-2">
              <div className="text-gray-500 text-xs">Start date</div>
              <div className="text-sm">
                {dayjs(task.current_start_date).format('DD-MM-YYYY, HH:mm')}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-gray-500 text-xs">Due date</div>
              <div className="text-sm">{dayjs(task.overdue_date).format('DD-MM-YYYY, HH:mm')}</div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-gray-500 text-xs">Assigned to</div>
            <div className="flex flex-wrap gap-6 text-sm">
              {task?.users?.length
                ? task.users.map(user => (
                    <div key={user.id} className="flex gap-2 items-center">
                      <Avatar
                        size="sm"
                        classNameLetters="text-sm"
                        src={user.avatar_url}
                        firstName={user.first_name}
                        lastName={user.last_name}
                      />
                      <span>
                        {user.first_name} {user.last_name}
                      </span>
                    </div>
                  ))
                : '-'}
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-gray-500 text-xs">Notified users when it gets complete</div>
            <div className="text-sm">
              {detailedTask?.task?.complete_subs?.length
                ? detailedTask.task.complete_subs.map(user => (
                    <div key={user.id} className="flex gap-2 items-center">
                      <Avatar
                        size="sm"
                        classNameLetters="text-sm"
                        src={user.avatar_url}
                        firstName={user.first_name}
                        lastName={user.last_name}
                      />
                      <span>
                        {user.first_name} {user.last_name}
                      </span>
                    </div>
                  ))
                : '-'}
            </div>
          </div>
          <div className="space-y-2">
            <div className="text-gray-500 text-xs">Description</div>
            <div className="text-sm">
              {task.description ? <TextWithShortLinks text={task.description} /> : '...'}
            </div>
          </div>
        </>
      ) : (
        <div className="h-25 flex items-center justify-center">
          <Spinner size="md" />
        </div>
      )}
    </div>
  );
};

export default TaskItemDetailedInfo;
